import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import Pagination from "./Pagination";
import CardList from "./CardList";
import './UserProfile.css';
import { useUser } from '../UserContext';
import moment from 'moment';
import { ProgressBar, Placeholder, Badge, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Link } from "react-router-dom";
import { buildUrlWithParams } from "./utils";
import ActiveFilters from './ActiveFilters';
import { useModeratorAction } from '../ModeratorActionContext';
import { FaShieldAlt, FaTrophy, FaUser, FaHeart } from 'react-icons/fa';

const categoryOrder = [
  "Creation",
  "Sharing and Interaction",
  "Engagement",
  "Rating",
  "Special Event",
  "Miscellaneous"
];

const UserProfile = () => {
  const { userId, tab } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [userCards, setUserCards] = useState([]);
  const [userFavorites, setUserFavorites] = useState([]);
  const [userComments, setUserComments] = useState([]);
  const [userAchievements, setUserAchievements] = useState([]);
  const [userContests, setUserContests] = useState([]);
  const [activeTab, setActiveTab] = useState(tab || 'cards');
  const [totalPages, setTotalPages] = useState(1);
  const { user, setUser, setSelectedCard, settings, setSettings, filters, setFilters } = useUser();
  const params = new URLSearchParams(location.search);
  const [currentPage, setCurrentPage] = useState(parseInt(params.get('page'), 10) || 1);
  const [loading, setLoading] = useState(true);
  const [achievementProgress, setAchievementProgress] = useState(0);
  const fileInputRef = useRef(null);

  const { openModModal } = useModeratorAction();

  const handleBanUser = (userId) => {
    openModModal('user', userId);
  };

  useEffect(() => {
    setCurrentPage(parseInt(params.get('page'), 10) || 1);
  }, [location]);

  useEffect(() => {
    setActiveTab(tab || 'cards');
  }, [tab]);

  useEffect(() => {
    setUserComments([]);
    setUserCards([]);
    setUserFavorites([]);
    setUserAchievements([]);
    setUserContests([]);
    const fetchData = async () => {
      try {
        const userDataResponse = await axios.get(`/api/users/user.php?userId=${userId}`);
        setUserData(userDataResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  useEffect(() => {
    const newFilters = {
        class: params.get('class') || '',
        cost: params.get('cost') || '',
        attack: params.get('attack') || '',
        health: params.get('health') || '',
        category: params.get('category') || '',
        cardtype: params.get('cardtype') || '',
        search: params.get('search') || '',
        startDate: params.get('startDate') || '',
        endDate: params.get('endDate') || '',
        order: params.get('order') || '',
    };

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
        setCurrentPage(1);
        setFilters(newFilters);
    }
    setCurrentPage(parseInt(params.get('page'), 10) || 1);
}, [location]);

  useEffect(() => {
    if (filters.length === 0) {
      return;
    }
    if (currentPage === 0) {
      return;
    }
    const fetchData = async () => {
      try {
        // Fetch data based on active tab
        if (activeTab === "comments") {
          const commentsResponse = await axios.get(`/api/users/user_comments.php?userId=${userId}&page=${currentPage}`);
          setUserComments(commentsResponse.data.comments);
          setTotalPages(commentsResponse.data.totalPages);
        } else if (activeTab === "cards") {
          const cardsResponse = await axios.get(`/api/json_custom_react.php`, {
            params: {
              search: filters.search,
              page: currentPage,
              class: filters.class,
              mana: filters.cost,
              attack: filters.attack,
              health: filters.health,
              deckid: '500',
              user_id: userId,
              startDate: filters.startDate,
              endDate: filters.endDate,
              cardtype: filters.cardtype,
              language: settings.language,
              order: filters.order,
            }
          });

          setUserCards(cardsResponse.data.cards);
          setTotalPages(cardsResponse.data.main.maxpage);
        } else if (activeTab === "favorites") {
          const favoritesResponse = await axios.get(`/api/json_custom_react.php?deckid=501&user_id=${userId}&page=${currentPage}`);
          setUserFavorites(favoritesResponse.data.cards);
          setTotalPages(favoritesResponse.data.main.maxpage);
        } else if (activeTab === "achievements") {
          const achievementsResponse = await axios.get(`/api/users/user_achievements.php?userId=${userId}`);
          const achievements = achievementsResponse.data.achievements;

          // Exclude achievements with ids 33, 34, 35 from the calculation
          const filteredAchievements = achievements.filter(
            (achievement) => ![33, 34, 35].includes(Number(achievement.id))
          );
          
          const totalAchievements = filteredAchievements.length;
          const earnedAchievements = filteredAchievements.filter(a => Number(a.earned) === 1).length;
  
          const progressPercentage = totalAchievements > 0 ? (earnedAchievements / totalAchievements) * 100 : 0;
          setAchievementProgress(progressPercentage);
  
          setUserAchievements(achievements);
        } else if (activeTab === "contests") {
          const contestsResponse = await axios.get(`/api/users/user_contests_participation.php?userId=${userId}`);
          setUserContests(contestsResponse.data.contests);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, activeTab, currentPage, filters]);

    // Memoize the grouping of achievements to avoid re-calculating on every render
    const groupedAchievements = useMemo(() => {
      if (!userAchievements || userAchievements.length === 0) {
          return {};
      }
      // Group achievements by their category property
      return userAchievements.reduce((acc, achievement) => {
          const category = achievement.category || 'General'; // Use 'General' as a fallback
          if (!acc[category]) {
              acc[category] = [];
          }
          acc[category].push(achievement);
          return acc;
      }, {});
    }, [userAchievements]);

  const handleCategoryChange = (category) => {
    setTotalPages(0);
    setActiveTab(category);
    setCurrentPage(1);
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("userId", userId);

      try {
        const response = await axios.post("/api/users/upload_image.php", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data.success) {
          setUserData({ ...userData, profile_picture: response.data.imageUrl });
          setUser({
            ...user,
            profile_picture: response.data.imageUrl,
          });
        } else {
          console.error("Image upload failed:", response.data.message);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };

  const renderProfilePlaceholder = () => (
    <div className="d-flex align-items-center mb-3 mx-2 mx-md-4 pt-4">
      <div className="me-3 col-auto align-self-start">
        <Placeholder
          animation="glow"
          className="rounded-circle"
          style={{
            width: "130px",
            height: "130px",
            display: "inline-block"
          }}
        >
          <Placeholder xs={12} style={{ height: "100%" }} />
        </Placeholder>
      </div>
      <div className="text-start col">
        <Placeholder as="h3" animation="glow">
          <Placeholder xs={6} />
        </Placeholder>
        <Placeholder as="p" animation="glow">
          <Placeholder xs={8} /> <Placeholder xs={4} />
        </Placeholder>
        <Placeholder as="p" animation="glow">
          <Placeholder xs={6} /> <Placeholder xs={5} />
        </Placeholder>
      </div>
    </div>
  );

  const renderContentPlaceholder = () => (
    <div className="pt-3">
      <Placeholder as="div" animation="glow">
        <Placeholder xs={12} size="lg" className="mb-3" />
      </Placeholder>
    </div>
  );

  // Function to render placement badge with appropriate color
  const renderPlacementBadge = (placement) => {
    let badgeClass = "bg-secondary";
    
    if (placement === 1) {
      badgeClass = "bg-warning text-dark"; // Gold for 1st place
    } else if (placement === 2) {
      badgeClass = "bg-light text-dark"; // Silver for 2nd place
    } else if (placement === 3) {
      badgeClass = "bg-danger"; // Bronze for 3rd place
    }
    
    return (
      <Badge className={badgeClass}>
        {placement === 1 ? (
          <><FaTrophy className="me-1" /> 1st Place</>
        ) : placement === 2 ? (
          <><FaTrophy className="me-1" /> 2nd Place</>
        ) : placement === 3 ? (
          <><FaTrophy className="me-1" /> 3rd Place</>
        ) : (
          <>#{placement}</>
        )}
      </Badge>
    );
  };

  return (
    <div>
      {loading ? renderProfilePlaceholder() : (
        userData && (
          <div className="d-flex align-items-center mb-3 mx-2 mx-md-4 pt-4">
            <div className="me-3 position-relative profile-image-container col-auto align-self-start">
              <img
                src={
                  userData.profile_picture 
                    ? (userData.profile_picture.includes('http') 
                      ? userData.profile_picture 
                      : `/images/users/pictures/${userData.profile_picture}`)
                    : "/gallery/images/profile-image.jpg"
                }
                
                alt="Profile"
                className="rounded-circle img-thumbnail"
                style={{ width: "130px", height: "130px", objectFit: "cover" }}
              />
              {user && Number(userData.id) === Number(user.userid) && (
                <div className="edit-overlay user-select-none" onClick={handleImageClick} style={{ cursor: "pointer" }}>
                  Edit
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>
            <div className="text-start col">
              <h3>{userData.username}
              {user && user.mod === true && Number(user.userid) !== Number(userData.id) && (	
                <button className="btn btn-danger btn-sm ms-2 mb-1" onClick={() => handleBanUser(userData.id)}><FaShieldAlt /> Ban User</button>
              )}
              </h3>
              {userData.regDate !== 0 && userData.regDate && (
                <p>
                  <strong>Account Created:</strong> {userData.regDate ? moment.unix(userData.regDate).fromNow() : 'N/A'}
                </p>
              )}
              <p>
                <strong>Total Cards:</strong> {userData.total_cards} <strong>Gallery Cards:</strong> {userData.gallery_cards}
              </p>
              <p>
                <strong>Average Rating of Gallery Cards:</strong> {userData.avg_rating ? userData.avg_rating.toFixed(2) : 'N/A'}
              </p>
            </div>
          </div>
        )
      )}
      <ul className="user-profile-tabs nav nav-tabs mx-2 mx-md-4">
        <li className="nav-item">
          <Link to={`/user/${userId}/cards`} style={{ textDecoration: 'none', color: 'inherit' }}
            className={`nav-link ${activeTab === "cards" ? "active" : ""}`}
            onClick={() => handleCategoryChange("cards")}>
            Cards
          </Link>
        </li>
        <li className="nav-item">
          <Link to={`/user/${userId}/comments`} style={{ textDecoration: 'none', color: 'inherit' }}
            className={`nav-link ${activeTab === "comments" ? "active" : ""}`}
            onClick={() => handleCategoryChange("comments")}>
            Comments
          </Link>
        </li>
        <li className="nav-item">
          <Link to={`/user/${userId}/favorites`} style={{ textDecoration: 'none', color: 'inherit' }}
            className={`nav-link ${activeTab === "favorites" ? "active" : ""}`}
            onClick={() => handleCategoryChange("favorites")}>
            Favorites
          </Link>
        </li>
        <li className="nav-item">
          <Link to={`/user/${userId}/contests`} style={{ textDecoration: 'none', color: 'inherit' }}
            className={`nav-link ${activeTab === "contests" ? "active" : ""}`}
            onClick={() => handleCategoryChange("contests")}>
            Contests
          </Link>
        </li>
        <li className="nav-item">
          <Link to={`/user/${userId}/achievements`} style={{ textDecoration: 'none', color: 'inherit' }}
            className={`nav-link ${activeTab === "achievements" ? "active" : ""}`}
            onClick={() => handleCategoryChange("achievements")}>
            Achievements
          </Link>
        </li>
      </ul>
      {loading ? renderContentPlaceholder() : (
        <>
          {activeTab === "about" && (
            <div>
              <h3>About</h3>
              <p>{userData ? userData.about : "Loading..."}</p>
            </div>
          )}
          {activeTab === "cards" && (
            <div className="pt-3">
              <ActiveFilters filters={filters} handleRemoveFilter={(filterName) => {
                setFilters({ ...filters, [filterName]: '' });
                setCurrentPage(1);
              }} />
              <CardList cards={userCards} loading={loading} settings={settings} />
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          )}
          {activeTab === "favorites" && (
            <div className="pt-3">
              <CardList cards={userFavorites} loading={loading} settings={settings} />
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          )}

{activeTab === "contests" && (
  <div className="pt-4 mt-3 mx-2 mx-md-4 pb-5 mb-5">
    <Row className="mb-5">
      <Col>
        
        {userContests.length === 0 ? (
          <div className="text-center">
            This user hasn't participated in any contests yet.
          </div>
        ) : (
          <Row>
            {userContests.map((contest) => {
              // Determine contest status
              const currentTime = Math.floor(Date.now() / 1000);
              const isSubmissionPhase = currentTime < contest.submissions_end_date;
              const isVotingPhase = !isSubmissionPhase && currentTime < contest.end_date;
              const isCompleted = currentTime >= contest.end_date;
              
              return (
                <Col key={contest.submission_id} xs={6} md={4} lg={2} className="mb-4">
                  <div className="contest-item h-100 d-flex flex-column">
                    <div className="ratio ratio-1x1">
                    <Link
                        to={`/contests/${contest.contest_id}`}
                      >
                      <img 
                        src={`/images/contest/sketch/${contest.contest_id}.svg`}
                        alt={contest.contest_title}
                        className="img-fluid"
                        style={{ objectFit: 'cover' }}
                      />
                                            </Link>
                    </div>
                    
                      {/* Fixed height container for badge */}
                      <div className="my-2" style={{ height: "24px" }}>
                        {isCompleted && contest.placement <= 3 ? (
                          <div className={`badge placement-badge placement-${contest.placement}`}>
                            <FaTrophy className="me-1" />
                            {contest.placement === 1 ? '1st Place' : 
                             contest.placement === 2 ? '2nd Place' : '3rd Place'}
                          </div>
                        ) : isCompleted ? (
                          <div className="badge placement-badge placement-other">#{contest.placement}</div>
                        ) : (
                          <div className="badge bg-primary">
                            {isSubmissionPhase ? 'Contest in Progress' : 'Voting Phase'}
                          </div>
                        )}
                      </div>

                    <div className="p-2 text-center d-flex flex-column">
                      {/* Fixed height container for title */}
                      <div className="contest-title-container" style={{ height: "40px", overflow: "hidden" }}>
                        <h5 className="fw-bold" style={{ fontSize: "1rem", margin: 0 }}>
                          {contest.contest_title}
                        </h5>
                      </div>
                      

                      
                      <div className="d-flex justify-content-around small text-muted mb-2">
                        <div className="d-flex align-items-center"><FaHeart className="me-1" />{contest.vote_count}</div>
                        <div className="d-flex align-items-center"><FaUser className="me-1" />{contest.total_submissions}</div>
                      </div>
                    </div>
                    
                    {/* Footer will always be at the bottom with mt-auto */}
                    <div className="p-2 text-center mt-auto">
                      <Link
                        to={buildUrlWithParams({ ...Object.fromEntries(new URLSearchParams(window.location.search)), selectedCard: contest.card_id })}
                        className="btn btn-sm btn-primary"
                      >
                        View Submission
                      </Link>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
      </Col>
    </Row>
  </div>
)}

{activeTab === "achievements" && (
  <div className="pt-3 mt-0 px-2 px-md-4">
    {Object.keys(groupedAchievements).length === 0 ? (
      <div className="text-center p-5">This user has no achievements yet.</div>
    ) : (
      categoryOrder.map((category) => {
        const achievements = groupedAchievements[category];
        if (!achievements || achievements.length === 0) {
          return null;
        }

        const totalCount = achievements.length;
        const earnedCount = achievements.filter(a => a.earned_at).length;

        return (
          <div key={category} className="mb-4">
            <h3 className="text-center my-5">
              {category}
              {category !== 'Miscellaneous' && ` (${earnedCount}/${totalCount})`}
            </h3>
            <Row className="g-2 g-sm-3 px-0 px-lg-4">
              {achievements.map((achievement) => {
                const isEarned = achievement.earned_at;
                const hasCardLink = achievement.cardid && achievement.usercardid && isEarned;

                // NEW: Determine if the achievement has progress data
                const hasProgress = !achievement.earned_at && achievement.requirement && Number(achievement.requirement) > 0;
                
                // NEW: Calculate the percentage for the progress bar
                const progressPercent = hasProgress
                  ? ( (Number(achievement.progress) || 0) / Number(achievement.requirement) ) * 100
                  : 0;

                const achievementContent = (
                  <div
                    className="contest-frame h-100 d-flex flex-column text-center"
                    style={{ paddingTop: '30px', paddingBottom: '30px', opacity: isEarned ? 1 : 0.5, cursor: hasCardLink ? 'pointer' : 'default' }}
                  >
                    <div className="mb-2">
                      <img
                        src={`/images/trophy-${achievement.trophytype}-alt.webp`}
                        alt={achievement.name}
                        className="img-fluid p-2"
                        style={{ maxHeight: '200px' }}
                      />
                    </div>
                    <div className="text-center">
                      <h6 className="fw-bold" style={{ fontSize: "0.9rem", margin: 0 }}>
                        {achievement.name}
                      </h6>
                      <p className="small text-muted mb-1 px-1" style={{ minHeight: '40px' }}>
                        {achievement.description}
                      </p>
</div>
                      {/* NEW: Conditional rendering for earned date OR progress bar */}
                      <div className="mt-auto px-3 pt-1" style={{ minHeight: '30px' }}>
                        {achievement.earned_at ? (
                          // If earned, show the date and rarity
                          <p className="small text-success mb-0" style={{ fontSize: '0.75rem' }}>
                            Earned: {moment.unix(achievement.earned_at).format("L")} ({achievement.percent}%)
                          </p>
                        ) : hasProgress ? (
                          // If not earned but has progress, show the ProgressBar
                          // <ProgressBar 
                          //   now={progressPercent}
                          //   label={`${Number(achievement.progress) || 0} / ${achievement.requirement}`}
                          //   variant="info"
                          //   animated
                          //   style={{ height: '1.2rem', fontSize: '0.7rem' }}
                          // />
                          // MODIFIED ProgressBar SECTION
                          <div style={{ position: 'relative', height: '1.2rem' }}> {/* Wrapper for positioning */}
                            <ProgressBar 
                              now={progressPercent}
                              // label prop removed
                              variant="info"
                              animated
                              style={{ 
                                height: '1.2rem', 
                                // fontSize on ProgressBar itself is for its internal label, which we're not using
                              }}
                            />
                            <div 
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'black', // Text color black
                                fontSize: '0.7rem', // Your desired font size for the text
                                textAlign: 'center', // Ensure text is centered
                                // lineHeight: '1.2rem' // Can also help vertical alignment if text is single line
                              }}
                            >
                              {`${Number(achievement.progress) || 0} / ${achievement.requirement}`}
                            </div>
                          </div>
                        ) : (
                          // If not earned and no progress (binary achievement), this space is empty
                          // The minHeight on the parent div keeps the layout consistent
                          null
                        )}
                      </div>

                    </div>
                );

                return (
                  <Col key={achievement.id} xs={6} lg={3}>
                    {hasCardLink ? (
                      <Link
                        to={buildUrlWithParams({ ...Object.fromEntries(new URLSearchParams(location.search)), selectedCard: achievement.cardid })}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {achievementContent}
                      </Link>
                    ) : (
                      achievementContent
                    )}
                  </Col>
                );
              })}
            </Row>
          </div>
        );
      })
    )}
  </div>
)}
          {activeTab === "comments" && (
            <div className="pt-5 px-2 px-md-4">
              {userComments.map((comment) => (
                <div key={comment.id} className="d-flex mb-3">
                  <div className="me-3">
                    <Link
                      to={buildUrlWithParams({ ...Object.fromEntries(new URLSearchParams(window.location.search)), selectedCard: comment.card_id })}
                      style={{ textDecoration: 'none' }}
                    ><img
                        src={`/cards/${comment.cardid}.webp`}
                        alt="User"
                        className=""
                        style={{ height: "200px", width: "auto" }}
                      /></Link>
                  </div>
                  <div className="user-profile-comment text-start">
                    <p>{comment.comment}</p>
                    <p>
                      <strong>Date:</strong>{" "}
                      {new Date(comment.date * 1000).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserProfile;